<template>
  <div id="map-wrapper" class="z4">
    <div class="map-loading caps">Loading...</div>
    <div id="map" class="z4" v-if="investigations"></div>
    <close-button :onClick="toggleMap"></close-button>
  </div>
</template>
<template>
  <div id="map-wrapper" class="z4">
    <div class="map-loading caps">Loading...</div>
    <div id="map" class="z4" v-if="investigations"></div>
    <close-button :onClick="toggleMap"></close-button>
  </div>
</template>

<script>
import "mapbox-gl/src/css/mapbox-gl.css";
import CloseButton from "@/components/CloseButton";
import mapboxgl from "mapbox-gl";
import { incidentDates, isStudy } from "@/utils";
import { mapActions, mapState, mapGetters } from "vuex";

export default {
  name: "Map",

  data() {
    return {
      mapboxToken:
        "pk.eyJ1IjoiZmEtd2ViIiwiYSI6ImNqczJiZXJjOTA1NGs0Ym54cTB0NTAyNGQifQ.EjulB2cnGZgW6RqeNne7Hw",
      map: false,
    };
  },

  components: { CloseButton },

  mounted() {
    this.$nextTick(() => {
      this.initMap();
    });
  },

  computed: {
    ...mapState(["investigations", "mapLocations"]),
    ...mapGetters(["getInvestigationBySlug"]),
  },

  watch: {
    investigations() {
      this.$nextTick(() => {
        this.initMap();
      });
    },
  },

  methods: {
    ...mapActions(["toggleMap"]),

    mediaHTML(inv) {
      var videoLink =
        inv && inv.preview_video && inv.preview_video.link
          ? inv.preview_video.link
          : null;
      var imageLink =
        inv && inv.preview_image && inv.preview_image.link
          ? inv.preview_image.link
          : null;

      if (videoLink) {
        // IMPORTANT: no real src; we set it on popup "open"
        return (
          '<video class="mt1" ' +
          'data-reference="' + inv.id + '" ' +
          'data-src="' + videoLink + '" ' +
          'muted autoplay loop playsinline preload="none"></video>'
        );
      } else if (imageLink) {
        return (
          '<img class="mt1" src="' + imageLink + '" alt="' +
          (inv.title || "preview") + ' preview" loading="lazy" />'
        );
      }
      return "";
    },

    initMap() {
      if (this.map) return;

      mapboxgl.accessToken = this.mapboxToken;
      this.map = new mapboxgl.Map({
        container: "map",
        style: "mapbox://styles/fa-web/cjs50kubo136r1foftea5juec",
        center: [25, 30],
        zoom: (1.9 * window.innerWidth) / 1920,
      });

      var self = this;

      (this.mapLocations || []).forEach(function (location) {
        // Robust guards for location payloads
        var loc = location && location.location;
        if (!loc || typeof loc !== "object") return;
        var lng = loc.lng, lat = loc.lat;
        if (!isFinite(lng) || !isFinite(lat)) return;

        var inv = self.getInvestigationBySlug(location.slug);
        if (!inv) return;

        var isInvestigation = !isStudy(inv);

        // Marker element
        var markerEl = document.createElement("div");
        markerEl.className = isInvestigation ? "marker red" : "marker yellow";
        markerEl.dataset.reference = inv.id;

        // Popup HTML with safe media fallback
        var media = self.mediaHTML(inv);
        var html =
          '<a href="/investigation/' + inv.slug + '">' +
            '<h3 class="caps mb1">' +
              '<span class="red">i.' + inv.code + "</span>&nbsp;" + inv.title +
            "</h3>" +
            media +
            '<div class="clearfix border xs-hide">' +
              '<div class="col col-6 p2">' +
                "<div class='caps fs-xs m0 label'>Date of Incident</div>" +
                incidentDates(inv) +
              "</div>" +
              '<div class="col col-6 border-left p2 xs-hide">' +
                "<div class='caps fs-xs m0 label'>Location</div>" +
                (inv.location_title || "") +
              "</div>" +
            "</div>" +
            '<div class="border-left border-bottom border-right p2 xs-hide">' +
              (inv.abstract || "") +
            "</div>" +
          "</a>";

        // Create popup and bind events to THIS popup (Mapbox GL 0.53: use e.target._content)
        var popup = new mapboxgl.Popup({ offset: 12 }).setHTML(html);

        popup.on("open", function (e) {
          var root = e && e.target && e.target._content ? e.target._content : null;
          if (!root || !root.querySelector) return;
          var vid = root.querySelector("video");
          if (!vid) return;

          // Assign the real src lazily
          if (!vid.src && vid.dataset && vid.dataset.src) {
            vid.src = vid.dataset.src;
          }

          // iOS/Safari autoplay helpers
          vid.muted = true;
          vid.setAttribute("playsinline", "");
          try {
            var p = vid.play();
            if (p && typeof p.catch === "function") p.catch(function () {});
          } catch (err) {}

          // Optional: if the URL later errors (expired token), swap to image if present
          var imgEl = root.querySelector("img");
          var imgSrc = imgEl && imgEl.getAttribute ? imgEl.getAttribute("src") : null;
          if (imgSrc) {
            vid.addEventListener("error", function () {
              var img = document.createElement("img");
              img.className = "mt1";
              img.src = imgSrc;
              img.alt = vid.getAttribute("alt") || "preview";
              if (vid.parentNode) vid.parentNode.replaceChild(img, vid);
            }, { once: true });
          }
        });

        popup.on("close", function (e) {
          var root = e && e.target && e.target._content ? e.target._content : null;
          if (!root || !root.querySelector) return;
          var vid = root.querySelector("video");
          if (vid) {
            try { vid.pause(); } catch (err) {}
            vid.removeAttribute("src");
            try { vid.load(); } catch (err) {}
          }
        });

        new mapboxgl.Marker(markerEl)
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(self.map);
      });
    },
  },
};
</script>

<style lang="css">
@keyframes fadein {
  0% { opacity: 0; }
  60% { opacity: 0; }
  100% { opacity: 1; }
}
@-webkit-keyframes fadein {
  0% { opacity: 0; }
  60% { opacity: 0; }
  100% { opacity: 1; }
}

#map-wrapper {
  background: var(--black);
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 10000;
  .close-button { z-index: 10001; }
}

.map-loading {
  position: fixed;
  top: 49%;
  width: 100%;
  color: var(--red);
  letter-spacing: 0;
  text-align: center;
}

#map {
  -webkit-animation: 1.5s ease-out 0s normal forwards 1 fadein;
  animation: 1.5s ease-out 0s normal forwards 1 fadein;
  background: var(--black);
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  overflow: hidden;

  h1, h2, h3, h4, h5, h6 {
    font-size: var(--font-size-s);
    line-height: 1;
    font-weight: regular;
    font-size: 1em;
  }

  .marker {
    background-size: cover;
    width: 12px; height: 12px;
    border-radius: 50%;
    cursor: pointer;
  }
  .marker:hover { border: 2px solid white; }
  .marker.red { background-image: url("../assets/marker-red.svg"); }
  .marker.yellow { background-image: url("../assets/marker-yellow.svg"); }

  .mapboxgl-popup-content {
    font-family: var(--font-sans);
    font-size: var(--font-size-s);
    line-height: 1.3;
    background: var(--col-light);
    border-radius: 0;
    padding: 1.5em;
    width: 30vw; max-width: 530px;

    .mapboxgl-popup-close-button { display: none; }

    video, img {
      max-width: 100%;
      width: 100%;
      display: block;
    }
    video + div, img + div { margin-top: -4px; }

    .label { margin-bottom: 0.3em; }
  }

  .mapboxgl-popup-tip { border-top-color: var(--col-light); }
  .mapboxgl-ctrl-bottom-right { font-size: 8px; line-height: 1.4; }
}

@media (--phone) {
  #map {
    .marker { width: 18px; height: 18px; }
    .mapboxgl-popup-content { padding: 0.75em; }
  }
}
</style>

import { incidentDates, isStudy } from "@/utils";
import { mapActions, mapState, mapGetters } from "vuex";

export default {
  name: "Map",

  data() {
    return {
      mapboxToken:
        "pk.eyJ1IjoiZmEtd2ViIiwiYSI6ImNqczJiZXJjOTA1NGs0Ym54cTB0NTAyNGQifQ.EjulB2cnGZgW6RqeNne7Hw",
      map: false,
    };
  },

  components: { CloseButton },

  mounted() {
    this.$nextTick(() => {
      this.initMap();
    });
  },

  computed: {
    ...mapState(["investigations", "mapLocations"]),
    ...mapGetters(["getInvestigationBySlug"]),
  },

  watch: {
    investigations() {
      this.$nextTick(() => {
        this.initMap();
      });
    },
  },

  methods: {
    ...mapActions(["toggleMap"]),

    // Build media HTML: prefer video (data-src only), else image, else nothing.
    mediaHTML(inv) {
      var videoLink =
        inv && inv.preview_video && inv.preview_video.link
          ? inv.preview_video.link
          : null;
      var imageLink =
        inv && inv.preview_image && inv.preview_image.link
          ? inv.preview_image.link
          : null;

      if (videoLink) {
        // IMPORTANT: no real src yet — we load it in popup "open".
        return (
          '<video class="mt1" ' +
          'data-reference="' +
          inv.id +
          '" ' +
          'data-src="' +
          videoLink +
          '" ' +
          'muted autoplay loop playsinline preload="none"></video>'
        );
      } else if (imageLink) {
        return (
          '<img class="mt1" src="' +
          imageLink +
          '" alt="' +
          (inv.title || "preview") +
          ' preview" loading="lazy" />'
        );
      }
      return "";
    },

    initMap() {
      if (this.map) return;

      mapboxgl.accessToken = this.mapboxToken;
      this.map = new mapboxgl.Map({
        container: "map",
        style: "mapbox://styles/fa-web/cjs50kubo136r1foftea5juec",
        center: [25, 30],
        zoom: (1.9 * window.innerWidth) / 1920,
      });

      var self = this;

      (this.mapLocations || []).forEach(function (location) {
        // Robust location guards
        var loc = location && location.location;
        if (!loc || typeof loc !== "object") return;
        var lng = loc.lng;
        var lat = loc.lat;
        if (!isFinite(lng) || !isFinite(lat)) return;

        var inv = self.getInvestigationBySlug(location.slug);
        if (!inv) return;

        var isInvestigation = !isStudy(inv);

        // Marker element
        var markerEl = document.createElement("div");
        markerEl.className = isInvestigation ? "marker red" : "marker yellow";
        markerEl.dataset.reference = inv.id;

        // Popup HTML with safe media
        var media = self.mediaHTML(inv);
        var html =
          '<a href="/investigation/' +
          inv.slug +
          '">' +
          '<h3 class="caps mb0">' +
          '<span class="red">i.' +
          inv.code +
          "</span>&nbsp;" +
          inv.title +
          "</h3>" +
          media +
          '<div class="clearfix border xs-hide">' +
          '<div class="col col-6 p2">' +
          "<div class='caps fs-xs m0 label'>Date of Incident</div>" +
          incidentDates(inv) +
          "</div>" +
          '<div class="col col-6 border-left p2 xs-hide">' +
          "<div class='caps fs-xs m0 label'>Location</div>" +
          (inv.location_title || "") +
          "</div>" +
          "</div>" +
          '<div class="border-left border-bottom border-right p2 xs-hide">' +
          (inv.abstract || "") +
          "</div>" +
          "</a>";

        // Create popup and bind events to the POPUP (not the marker)
        var popup = new mapboxgl.Popup({ offset: 12 }).setHTML(html);

        // On open: assign src and autoplay (iOS/Safari safe)
        popup.on("open", function () {
          var root = document.getElementsByClassName(
            "mapboxgl-popup-content"
          )[0];
          if (!root || !root.querySelector) return;
          var vid = root.querySelector("video");
          if (!vid) return;

          // Assign the real src lazily
          if (!vid.src && vid.dataset && vid.dataset.src) {
            vid.src = vid.dataset.src;
          }

          // Autoplay policy helpers
          vid.muted = true; // property matters on iOS
          vid.setAttribute("playsinline", "");
          // Kick playback; ignore rejection if policy blocks
          try {
            var p = vid.play();
            if (p && typeof p.catch === "function") p.catch(function () {});
          } catch (e) {}

          // Optional: if this URL later errors (e.g., expired token), swap to image if available
          var imgEl = root.querySelector("img");
          var imgSrc =
            imgEl && imgEl.getAttribute ? imgEl.getAttribute("src") : null;
          if (imgSrc) {
            vid.addEventListener(
              "error",
              function () {
                var img = document.createElement("img");
                img.className = "mt1";
                img.src = imgSrc;
                img.alt = vid.getAttribute("alt") || "preview";
                if (vid.parentNode) vid.parentNode.replaceChild(img, vid);
              },
              { once: true }
            );
          }
        });

        // On close: pause & remove src to stop network/memory usage
        popup.on("close", function () {
          var root = popup.getElement ? popup.getElement() : null;
          if (!root || !root.querySelector) return;
          var vid = root.querySelector("video");
          if (vid) {
            try {
              vid.pause();
            } catch (e) {}
            vid.removeAttribute("src"); // drop connection
            try {
              vid.load();
            } catch (e) {} // cancel fetch if in-flight
          }
        });

        new mapboxgl.Marker(markerEl)
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(self.map);
      });
    },
  },
};
</script>

<style lang="css">
@keyframes fadein {
  0% {
    opacity: 0;
  }
  60% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@-webkit-keyframes fadein {
  0% {
    opacity: 0;
  }
  60% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

#map-wrapper {
  background: var(--black);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;

  .close-button {
    z-index: 10001;
  }
}

.map-loading {
  position: fixed;
  top: 49%;
  width: 100%;
  color: var(--red);
  letter-spacing: 0;
  text-align: center;
}

#map {
  -webkit-animation: 1.5s ease-out 0s normal forwards 1 fadein;
  animation: 1.5s ease-out 0s normal forwards 1 fadein;
  background: var(--black);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: var(--font-size-s);
    line-height: 1;
    font-weight: regular;
    font-size: 1em;
  }

  .marker {
    background-size: cover;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    cursor: pointer;
  }
  .marker:hover {
    border: 2px solid white;
  }
  .marker.red {
    background-image: url("../assets/marker-red.svg");
  }
  .marker.yellow {
    background-image: url("../assets/marker-yellow.svg");
  }

  .mapboxgl-popup-content {
    font-family: var(--font-sans);
    font-size: var(--font-size-s);
    line-height: 1.3;
    background: var(--col-light);
    border-radius: 0;
    padding: 1.5em;
    width: 30vw;
    max-width: 530px;

    .mapboxgl-popup-close-button {
      display: none;
    }

    /* Media sizing: images behave like videos */
    video,
    img {
      max-width: 100%;
      width: 100%;
      display: block;
    }
    video + div,
    img + div {
      margin-top: -4px;
    }

    .label {
      margin-bottom: 0.3em;
    }
  }

  .mapboxgl-popup-tip {
    border-top-color: var(--col-light);
  }
  .mapboxgl-ctrl-bottom-right {
    font-size: 8px;
    line-height: 1.4;
  }
}

@media (--phone) {
  #map {
    .marker {
      width: 18px;
      height: 18px;
    }
    .mapboxgl-popup-content {
      padding: 0.75em;
    }
  }
}
</style>
